import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { Employee, ListResponse } from './types'

const ROOT = '/employees'
const KEY = ['employees'] as const

export type EmployeesFilter = {
  organizationId?: string | null
  isActive?: boolean
}

function filtersKey(filter: EmployeesFilter) {
  return {
    organizationId: filter.organizationId ?? null,
    isActive: filter.isActive ?? null,
  }
}

export function useEmployees(filter: EmployeesFilter = {}) {
  const api = useApi()
  const key = filtersKey(filter)
  return useQuery({
    queryKey: [...KEY, key],
    queryFn: () =>
      api<ListResponse<Employee>>(
        `${ROOT}${buildQuery({
          organization_id: filter.organizationId ?? undefined,
          is_active: filter.isActive === undefined ? undefined : String(filter.isActive),
        })}`,
      ),
  })
}

export function flattenEmployees(data: ListResponse<Employee> | undefined): Employee[] {
  return data?.items ?? []
}

export type CreateEmployeeInput = {
  organizationId: string
  fullName: string
  position?: string | null
  isActive?: boolean
}

export function useCreateEmployee() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEmployeeInput) =>
      api<Employee>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type PatchEmployeeInput = {
  id: string
  patch: Partial<{
    fullName: string
    position: string | null
    isActive: boolean
  }>
}

export function usePatchEmployee() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: PatchEmployeeInput) =>
      api<Employee>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteEmployee() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
